import React from 'react';
import classNames from 'classnames';

import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { classColour } from 'Modules/Ferries/Utils/seatGrid';
import { BookingWizardData, fareFieldLeg, patchAllPassengers } from '../../BookingWizardData/BaseBookingWizardData';
import { PassengerCounts } from './tripClassFares';
import { perHeadLabel } from './fareLabels';
import ClassWizardPoints from '../Seating/ClassWizardPoints';
import ClassInfoPopover from './ClassInfoPopover';
import { usesTwelveHourClock } from 'Util/_HumanWritten/TimeUtils';

/**
 * The class every passenger on the booking is in, or `MIXED` when they are not all in the same one.
 *
 * <p>`MIXED` is the interesting case. Once a booker has split the party across classes on the
 * seating step, no single cell describes their booking — so the card stops claiming one is selected
 * and says what they actually have instead.</p>
 */
export const selectedClassId = (
	wizardData: BookingWizardData,
	leg: 'departure' | 'return' = 'departure',
): string | null | 'MIXED' => {
	const ids = new Set<string | undefined>();
	const fareLeg = fareFieldLeg(wizardData, leg);
	patchAllPassengers(wizardData, passenger => {
		// Per leg. The two sailings are sold separately and stored separately; reading one field for
		// both made a choice on the return card rewrite the departing one.
		ids.add(fareLeg === 'return' ? passenger.returningPassengerClassId : passenger.passengerClassId);
		return null;
	});

	if (ids.size === 0) {
		return null;
	}
	if (ids.size > 1) {
		return 'MIXED';
	}
	return [...ids][0] ?? null;
};

/**
 * The fare type every passenger who has one is in, or null where they differ or nobody has one.
 *
 * <p>A different question from <c>selectedClassId</c>, which describes the booking as it stands and
 * counts a passenger with no fare type as one of the spread. That is right for the cards — a party
 * with someone unassigned is not "all Comfort Flex" — but wrong for deciding what a newly added
 * traveller should inherit, which is the question "what has this party already chosen".</p>
 *
 * <p>Asked the other way round it answered too late to be any use: a party of two in Comfort Flex
 * reads as <c>MIXED</c> the instant a third passenger exists without one, so the newcomer fell
 * through to the vessel's default and split a party nobody had split — and the card then reported
 * mixed fare types over a choice the booker never made.</p>
 */
export const heldClassId = (
	wizardData: BookingWizardData,
	leg: 'departure' | 'return' = 'departure',
): string | null => {
	const ids = new Set<string>();
	const fareLeg = fareFieldLeg(wizardData, leg);
	patchAllPassengers(wizardData, passenger => {
		const id = fareLeg === 'return' ? passenger.returningPassengerClassId : passenger.passengerClassId;
		if (id) {
			ids.add(id);
		}
		return null;
	});

	return ids.size === 1 ? [...ids][0] : null;
};

interface HeaderProps {
	classPrices: TripClassPrice[];
	counts: PassengerCounts;
	/**
	 * The booking's current spread of fare types, where it has more than one.
	 *
	 * <p>A column of its own rather than a note under the table. Keeping the party as it is, is a
	 * choice of the same kind as moving everyone to Premium — it has a price, it differs between
	 * sailings, and it is the one most bookers editing a trip time actually want. A footnote makes
	 * it look like an explanation of the columns instead of one of them.</p>
	 */
	mix?: string | null;
	/** Editing a booking, where every figure below is a difference rather than a price. */
	isAlteration?: boolean;
}

/**
 * The column headers, drawn once above the day's sailings.
 *
 * <p>This is where a class's name, colour and per-head fare live — repeating them on every card
 * would be the same three facts five times over. It shares <c>FareTripCard</c>'s grid, so the
 * headings sit over their columns by construction rather than by a width that has to be kept in
 * agreement with the card below.</p>
 */
export function TripFareHeader({ classPrices, counts, mix, isAlteration }: HeaderProps) {
	if (classPrices.length === 0) {
		return null;
	}

	const partySize = Object.values(counts).reduce((total, count) => total + count, 0);

	return (
		<div
			className={classNames('fare-card fare-card--header', { 'fare-card--twelve-hour': usesTwelveHourClock() })}
			style={{ '--fare-columns': classPrices.length + (mix ? 1 : 0) } as React.CSSProperties}
		>
			{/*
				The rail needs no heading — a green bar explains itself. The journey column carries
				what every figure to its right is a total OF, which a column of party prices otherwise
				leaves the booker to infer from the passenger count in the step's own subheading.
			*/}
			<div aria-hidden="true" />
			<div className="fare-card__colwho">
				{/*
					What the figures below are. On an edit they are not totals at all — the booking
					is paid for and every cell prints the difference taking that option would make —
					so calling them totals was the one thing on the row that was wrong.
				*/}
				{partySize > 0 && (isAlteration
					? `Price change for ${partySize} passenger${partySize === 1 ? '' : 's'}`
					: `Totals for ${partySize} passenger${partySize === 1 ? '' : 's'}`)}
			</div>

			{mix && (
				<div className="fare-card__colhead fare-card__colhead--mix">
					<span className="fare-card__colname">Keep current mix</span>
					<span className="fare-card__colsub">{mix}</span>
				</div>
			)}

			{classPrices.map((classPrice, index) => (
				<div key={classPrice.passengerClassId} className="fare-card__colhead">
					<span className="fare-card__colname">
						<span
							className="fare-card__swatch"
							style={{ background: classColour(index, classPrice.colour) }}
						/>
						{classPrice.name}
						<ClassInfoPopover
							classPrice={classPrice}
							counts={counts}
							colour={classColour(index, classPrice.colour)}
						/>
					</span>
					<ClassWizardPoints attributes={classPrice.attributes} />
					<span className="fare-card__colsub">{perHeadLabel(classPrice, counts)}</span>
				</div>
			))}
		</div>
	);
}
